import * as React from 'react';

import isPropValid from '@emotion/is-prop-valid';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

import * as iconClose from '~/assets/icons/icon_close.svg';
import * as iconCloseGray from '~/assets/icons/icon_close_gray.svg';
import OuterContainer from '~/components/Modal/OuterContainer';
import { ResponsiveBreakPoints } from '~/constants/responsive';
import { useOnClickOutside } from '~/hooks/useOnClickOutside';
import useStackablePortal, { isTopOfStack } from '~/hooks/useStackablePortal';
import { cssLengthUnits, type CSSLengthUnit } from '~/utils/css';

type ModalSize = 'auto' | 'full' | 'lg' | 'md' | 'mdl' | 'sm' | 'xl' | 'xs';

const innerContainerSizesMapping = {
  auto: 'auto',
  lg: '800px',
  md: '600px',
  mdl: '640px',
  sm: '500px',
  xl: '1000px',
  xs: '440px',
};

interface InnerContainerProps {
  isDialog?: boolean;
  minHeight?: CSSLengthUnit;
  scrollContent?: boolean;
  size?: ModalSize;
  verticallyCentered?: boolean;
}

const InnerContainer = styled('div', {
  shouldForwardProp: isPropValid,
})<InnerContainerProps>`
  position: relative;
  background-color: white;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  box-shadow: 0px 4px 25px 4px rgba(0, 0, 0, 0.3);
  text-align: left;
  overflow: auto;

  ${(props) => (props.verticallyCentered ? `top: 50%; transform: translateY(-50%)` : '')};

  ${(props) =>
    props.isDialog
      ? `
    width: 94%;
    margin: 50% 3% 0 3%;
    border-radius: 4px;
    `
      : `
    width: 100vw;
    height: 100vh;
  `}

  @media screen and (min-width: ${ResponsiveBreakPoints.ExtraSmallMobile}px) {
    border-radius: ${(props) => (props.size === 'full' ? '0px' : '4px')};
    min-height: ${(props) => props.minHeight};
    margin: ${(props) => (props.verticallyCentered ? '0 auto' : '1rem')};
    ${(props) => {
      switch (props.size) {
        case 'full':
          return `
            height: 100%;
            width: 100%;
            overflow-y: scroll;
            margin: 0;
          `;
        case 'auto':
          return `
              height: auto;
              width: calc(100% - 2rem);
            `;
        default:
          return `
            height: calc(100% - 2rem);
            width: calc(100% - 2rem);
          `;
      }
    }}
  }

  @media screen and (min-width: ${ResponsiveBreakPoints.Mobile}px) {
    overflow: visible;
    border-radius: ${(props) => (props.size === 'full' ? '0px' : '4px')};
    min-height: ${(props) => props.minHeight};
    margin: ${(props) => (props.verticallyCentered ? '0 auto' : '100px auto 30px')};
    ${(props) =>
      props.size === 'full'
        ? `
        height: 100%;
        width: 100%;
        overflow-y: scroll;
        margin: 0;
        `
        : `
        height: ${props.scrollContent ? '60vh' : 'auto'};
        width: ${
          props.size === 'auto'
            ? innerContainerSizesMapping['md']
            : innerContainerSizesMapping[props.size ?? 'md']
        };`}
  }
`;

const CloseIcon = styled('img')`
  position: absolute;
  top: -35px;
  right: 0;
  color: white;
  font-size: 16px;
  cursor: pointer;
  z-index: 1;
`;

const FullSizeContainerCloseIcon = styled(CloseIcon)<{ hasNoPaddingHeader?: boolean }>`
  top: 27px;
  right: 30px;
  @media screen and (min-width: ${ResponsiveBreakPoints.SmallMobile}px) {
    top: 30px;
  }
  ${(props) => (props.hasNoPaddingHeader ? 'top: 22px' : undefined)}
`;

const ResponsiveCloseIconContainer = styled('div')<{ isDialog?: boolean }>`
  display: ${(props) => (props.isDialog ? 'none' : 'inherit')};
  @media screen and (min-width: ${ResponsiveBreakPoints.Mobile}px) {
    display: none;
  }
`;

InnerContainer.defaultProps = {
  minHeight: cssLengthUnits.rem(12.5),
  size: 'md',
};

export interface ModalProps {
  alwaysHideCloseIcon?: boolean; // Only use if there's another way to close the modal
  backgroundColor?: string;
  children?: React.ReactNode;
  className?: string;
  closeIconE2ETag?: string;
  closeIconPendoTag?: string;
  closeOnClickOutside?: boolean;
  dataAnalyticsContext?: string;
  hasNoPaddingHeader?: boolean;
  isDialog?: boolean;
  isOpen: boolean;
  minHeight?: CSSLengthUnit;
  onClickedOut?: () => void;
  onClose?: (evt?) => void;
  scrollContent?: boolean;
  showCloseIcon?: boolean;
  size?: ModalSize;
  verticallyCentered?: boolean;
  zIndex?: number;
}

const Modal = ({
  alwaysHideCloseIcon,
  backgroundColor,
  children,
  className,
  closeIconE2ETag,
  closeIconPendoTag,
  closeOnClickOutside,
  dataAnalyticsContext,
  hasNoPaddingHeader,
  isDialog,
  isOpen,
  minHeight,
  onClickedOut,
  onClose,
  scrollContent,
  showCloseIcon,
  size,
  verticallyCentered,
  zIndex,
}: ModalProps) => {
  const { t } = useTranslation('translations');

  const closeIcon = showCloseIcon ? (
    <CloseIcon
      aria-label={t('dialog.close')}
      data-analytics={dataAnalyticsContext ? `${dataAnalyticsContext}-click-close` : undefined}
      data-e2e={closeIconE2ETag}
      data-pendo={closeIconPendoTag}
      onClick={onClose}
      role="button"
      src={iconClose}
    />
  ) : null;

  const portalKey = useStackablePortal(!!isOpen);

  const handleClose = React.useCallback(
    (evt?: MouseEvent | TouchEvent) => {
      if (!closeOnClickOutside) return;
      if (!isTopOfStack(portalKey)) return;

      onClose?.(evt);
      onClickedOut?.();
    },
    [portalKey, closeOnClickOutside, onClose, onClickedOut],
  );

  const renderFullSizeContainerCloseIcon = () =>
    alwaysHideCloseIcon ? null : (
      <FullSizeContainerCloseIcon
        aria-label={t('dialog.close')}
        data-analytics={dataAnalyticsContext ? `${dataAnalyticsContext}-click-close` : undefined}
        data-pendo={closeIconPendoTag}
        hasNoPaddingHeader={hasNoPaddingHeader}
        onClick={onClose}
        role="button"
        src={iconCloseGray}
      />
    );

  const shouldShowCloseIcon = size === 'full' && showCloseIcon;

  const innerContainerEl = React.useRef() as React.MutableRefObject<HTMLDivElement>;
  useOnClickOutside(innerContainerEl, handleClose);

  if (!isOpen) return null;
  return (
    <OuterContainer {...(zIndex && { zIndex })} {...(backgroundColor && { backgroundColor })}>
      <InnerContainer
        aria-modal="true"
        className={className}
        isDialog={isDialog}
        minHeight={minHeight}
        ref={innerContainerEl}
        role="dialog"
        scrollContent={scrollContent}
        size={size}
        verticallyCentered={verticallyCentered}
      >
        {shouldShowCloseIcon ? (
          renderFullSizeContainerCloseIcon()
        ) : (
          // Cheap media query
          <ResponsiveCloseIconContainer isDialog={isDialog}>
            {renderFullSizeContainerCloseIcon()}
          </ResponsiveCloseIconContainer>
        )}
        {closeIcon}
        {children}
      </InnerContainer>
    </OuterContainer>
  );
};

Modal.defaultProps = {
  closeOnClickOutside: true,
  isOpen: false,
};

export const MobileModal = styled(Modal)`
  position: fixed;
  top: 0;
  bottom: 0;

  @media screen and (min-width: ${ResponsiveBreakPoints.Mobile}px) {
    position: relative;
    top: auto;
    bottom: auto;
  }
`;

export default Modal;
